import React from 'react';
import isURL from 'validator/lib/isURL';

import Button from '@components/Button';
import Input from '@components/Input';
import ModalBase from '@components/ModalBase';

import { getEmptyErrorState } from '@utils/AppUtil';
import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';

export interface IGISDirectLinkModalProps {
  show: boolean;
  gisDirectURL: string;
  onClose: (e: React.SyntheticEvent<any>) => void;
  onSubmit: (gisDirectURL: string) => (e: React.SyntheticEvent<HTMLButtonElement>) => void;
}

interface IGISDirectLinkModalState {
  gisDirectURL: string;
  error: {
    gisDirectURL: IFieldValidation;
  };
}

const errMsgGen = new ErrorMessageGenerator();
const cyberdyneValidator = new CyberdyneValidator();

class GISDirectLinkModal extends React.Component<IGISDirectLinkModalProps, IGISDirectLinkModalState> {
  constructor(props: IGISDirectLinkModalProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  componentDidUpdate(prevProps: IGISDirectLinkModalProps) {
    if (prevProps.show !== this.props.show && this.props.show) {
      this.setState(this.getEmptyState());
    }
  }

  getEmptyState = (): IGISDirectLinkModalState => ({
    gisDirectURL: this.props.gisDirectURL || '',
    error: {
      gisDirectURL: getEmptyErrorState(),
    },
  });

  validateForm = () => {
    let isValid = true;
    const gisDirectURL = (this.state.gisDirectURL || '').trim();
    const { error } = this.getEmptyState();

    if (cyberdyneValidator.isEmptyValue(gisDirectURL)) {
      error.gisDirectURL = {
        errorState: IValidationState.ERROR,
        errorMessage: errMsgGen.getMandatoryFieldMessage('ESRI URL'),
      };

      isValid = false;
    } else if (cyberdyneValidator.isStringGreaterThanLength(gisDirectURL, 500)) {
      error.gisDirectURL = {
        errorState: IValidationState.ERROR,
        errorMessage: errMsgGen.getCharacterLimitMessage('ESRI URL', 500),
      };

      isValid = false;
    } else if (!isURL(gisDirectURL)) {
      error.gisDirectURL.errorState = IValidationState.ERROR;
      error.gisDirectURL.errorMessage = 'Enter valid ESRI URL.';
      isValid = false;
    }
    this.setState({ error });

    return isValid;
  };

  onSubmit = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    if (this.validateForm()) {
      const gisDirectURL = this.state.gisDirectURL.trim();
      this.props.onSubmit(gisDirectURL)(e);
    }
  };

  handleChange = (e: React.SyntheticEvent<HTMLInputElement>) => {
    const { name, value } = e.currentTarget;
    this.setState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  renderESRIUrlField = () => (
    <Input
      field={{
        label: 'Insert ESRI URL',
        type: CustomFieldType.TEXT,
        isRequired: true,
        value: this.state.gisDirectURL || '',
      }}
      error={this.state.error.gisDirectURL}
      width={4}
      name="gisDirectURL"
      onChange={this.handleChange}
    />
  );

  renderBody = () => <div className="gis-direct__body">{this.renderESRIUrlField()}</div>;

  renderFooter = () => (
    <div className="gis-direct__footer">
      <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.props.onClose} content="Cancel" />
      <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onSubmit} content="Save" />
    </div>
  );

  render() {
    return (
      <ModalBase
        show={this.props.show}
        onClose={this.props.onClose}
        titleElement={this.props.gisDirectURL ? 'Edit GIS Direct' : 'GIS Direct'}
        bodyElement={this.renderBody()}
        footerElement={this.renderFooter()}
        className="gis-direct-modal"
      />
    );
  }
}

export default GISDirectLinkModal;
